import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DebugUiBridge } from '../DebugUiBridge';
import type { DebugHudSnapshot, LightDebugInfo } from '../types';

/**
 * Lighting Panel
 * 
 * Standalone Lit component for lighting controls.
 * Includes ambient light toggle, active lights list, and debug visualization toggle.
 */
@customElement('lighting-panel')
export class LightingPanel extends LitElement {
  @property({ type: Boolean }) fullBrightMode = false;
  @property({ type: Boolean }) showLightGizmos = false;
  
  @state() private snapshot: DebugHudSnapshot | null = null;
  
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      color: white;
      font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
      padding: 16px;
      box-sizing: border-box;
      overflow: hidden;
    }
    
    .control-group {
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .control-group:last-of-type {
      border-bottom: none;
    }
    
    h3 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      opacity: 0.95;
    }
    
    .status-row {
      display: flex;
      gap: 10px;
      align-items: center;
      margin: 8px 0;
      font-size: 12px;
    }
    
    .status-row .label {
      min-width: 100px;
      opacity: 0.75;
    }
    
    .status-row .value {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
      opacity: 0.95;
    }
    
    label {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 0;
      cursor: pointer;
      user-select: none;
    }
    
    label:hover {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
      padding-left: 4px;
    }
    
    input[type="checkbox"] {
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
    
    .light-list {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      margin-top: 8px;
    }
    
    .light-sections {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }
    
    .light-section {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    
    .light-section.active {
      flex: 1;
    }
    
    .light-section.inactive {
      flex: 0 0 auto;
      max-height: 150px;
    }
    
    .light-section-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 0;
      font-size: 12px;
      font-weight: 600;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      margin-bottom: 4px;
    }
    
    .light-section-header .count {
      font-weight: normal;
      opacity: 0.6;
    }
    
    .light-section-header.active-header {
      color: #4caf50;
    }
    
    .light-section-header.inactive-header {
      color: #9e9e9e;
    }
    
    .light-item {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      gap: 4px;
      padding: 8px 10px;
      margin: 4px 0;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
      font-size: 11px;
      cursor: pointer;
      transition: background 0.15s ease;
    }
    
    .light-item:hover {
      background: rgba(255, 255, 255, 0.12);
    }
    
    .light-item-header {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
    }
    
    .light-item.disabled {
      opacity: 0.5;
    }
    
    .light-color-swatch {
      width: 12px;
      height: 12px;
      border-radius: 2px;
      flex-shrink: 0;
    }
    
    .light-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    }
    
    .light-type {
      font-size: 10px;
      padding: 2px 6px;
      border-radius: 3px;
      background: rgba(255, 255, 255, 0.1);
      flex-shrink: 0;
    }
    
    .light-type.combat {
      background: rgba(255, 100, 100, 0.3);
    }
    
    .light-type.scene {
      background: rgba(100, 200, 255, 0.3);
    }
    
    .light-type.character {
      background: rgba(100, 255, 100, 0.3);
    }
    
    .light-intensity {
      font-size: 10px;
      opacity: 0.7;
      flex-shrink: 0;
      min-width: 40px;
      text-align: right;
    }
    
    .light-details {
      display: flex;
      gap: 12px;
      font-size: 10px;
      opacity: 0.6;
      padding-left: 20px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    }
    
    .light-detail-item {
      display: flex;
      gap: 4px;
    }
    
    .light-detail-label {
      opacity: 0.7;
    }
    
    .no-lights {
      font-size: 11px;
      opacity: 0.5;
      font-style: italic;
      padding: 8px;
    }
  `;
  
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('himum:debug-ui-event', this.onDebugEvent as EventListener);
  }
  
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('himum:debug-ui-event', this.onDebugEvent as EventListener);
  }
  
  private onDebugEvent = (e: CustomEvent) => {
    const event = e.detail;
    
    switch (event.type) {
      case 'snapshot':
        this.snapshot = event.snapshot;
        break;
      case 'lighting:fullBright:changed':
        this.fullBrightMode = event.enabled;
        break;
      case 'lighting:gizmos:changed':
        this.showLightGizmos = event.enabled;
        break;
    }
  };
  
  private toggleFullBright() {
    this.fullBrightMode = !this.fullBrightMode;
    DebugUiBridge.dispatchCommand({
      type: 'lighting:fullBright:set',
      enabled: this.fullBrightMode
    });
  }
  
  private toggleLightGizmos() {
    this.showLightGizmos = !this.showLightGizmos;
    DebugUiBridge.dispatchCommand({
      type: 'lighting:gizmos:set',
      enabled: this.showLightGizmos
    });
  }
  
  private colorToHex(color: { r: number; g: number; b: number }): string {
    const r = Math.round(color.r * 255).toString(16).padStart(2, '0');
    const g = Math.round(color.g * 255).toString(16).padStart(2, '0');
    const b = Math.round(color.b * 255).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }
  
  private handleLightHover(lightName: string) {
    if (this.showLightGizmos) {
      DebugUiBridge.dispatchCommand({
        type: 'lighting:highlight:set',
        lightName
      });
    }
  }
  
  private handleLightLeave() {
    DebugUiBridge.dispatchCommand({
      type: 'lighting:highlight:set',
      lightName: null
    });
  }
  
  private formatPosition(pos?: { x: number; y: number; z: number }): string {
    if (!pos) return '—';
    return `${pos.x.toFixed(1)}, ${pos.y.toFixed(1)}, ${pos.z.toFixed(1)}`;
  }
  
  private renderLightItem(light: LightDebugInfo) {
    const colorHex = this.colorToHex(light.color);
    const distStr = light.distanceToPlayer !== undefined 
      ? `${light.distanceToPlayer.toFixed(1)}m` 
      : '—';
    return html`
      <div 
        class="light-item ${light.enabled ? '' : 'disabled'}"
        @mouseenter=${() => this.handleLightHover(light.name)}
        @mouseleave=${this.handleLightLeave}
      >
        <div class="light-item-header">
          <div class="light-color-swatch" style="background: ${colorHex}"></div>
          <span class="light-name" title="${light.name}">${light.name}</span>
          <span class="light-type ${light.source}">${light.type}</span>
          <span class="light-intensity">I: ${light.intensity.toFixed(1)}</span>
        </div>
        <div class="light-details">
          <span class="light-detail-item">
            <span class="light-detail-label">Dist:</span>
            <span>${distStr}</span>
          </span>
          ${light.range !== undefined ? html`
            <span class="light-detail-item">
              <span class="light-detail-label">Range:</span>
              <span>${light.range.toFixed(1)}</span>
            </span>
          ` : ''}
          ${light.position ? html`
            <span class="light-detail-item">
              <span class="light-detail-label">Pos:</span>
              <span>${this.formatPosition(light.position)}</span>
            </span>
          ` : ''}
        </div>
      </div>
    `;
  }
  
  render() {
    const lights = this.snapshot?.lights;
    const lightDetails = lights?.details ?? [];
    
    // Sort by distance (closest first), then by name
    const sortedLights = [...lightDetails].sort((a, b) => {
      const distA = a.distanceToPlayer ?? Infinity;
      const distB = b.distanceToPlayer ?? Infinity;
      if (distA !== distB) return distA - distB;
      return a.name.localeCompare(b.name);
    });
    
    // Group lights by enabled state
    const activeLights = sortedLights.filter(l => l.enabled);
    const inactiveLights = sortedLights.filter(l => !l.enabled);
    
    return html`
      <div class="control-group">
        <h3>Summary</h3>
        <div class="status-row">
          <span class="label">Active Lights:</span>
          <span class="value">${lights ? `${lights.enabled} / ${lights.total}` : '—'}</span>
        </div>
      </div>
      
      <div class="control-group">
        <h3>Debug Options</h3>
        <label>
          <input
            type="checkbox"
            ?checked=${this.fullBrightMode}
            @change=${this.toggleFullBright}
          />
          100% Lit (Full Bright)
        </label>
        <label>
          <input
            type="checkbox"
            ?checked=${this.showLightGizmos}
            @change=${this.toggleLightGizmos}
          />
          Show Light Gizmos
        </label>
      </div>
      
      <div class="light-sections">
        <div class="light-section active">
          <div class="light-section-header active-header">
            🟢 Active <span class="count">(${activeLights.length})</span>
          </div>
          <div class="light-list">
            ${activeLights.length > 0 
              ? activeLights.map(l => this.renderLightItem(l))
              : html`<div class="no-lights">No active lights</div>`
            }
          </div>
        </div>
        
        <div class="light-section inactive">
          <div class="light-section-header inactive-header">
            ⚫ Inactive <span class="count">(${inactiveLights.length})</span>
          </div>
          <div class="light-list">
            ${inactiveLights.length > 0 
              ? inactiveLights.map(l => this.renderLightItem(l))
              : html`<div class="no-lights">No inactive lights</div>`
            }
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lighting-panel': LightingPanel;
  }
}
